import { Upload } from 'lucide-react';
import { useId, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { cx } from '../../utils/cx';
import { Icon } from '../Icon/Icon';
import s from './FileDropzone.module.css';

export type FileDropzoneProps = {
  /** e.g. ".txt,.md,.csv,.pdf,image/png,image/jpeg,image/webp" */
  accept: string;
  /** Max size in bytes. */
  maxSize: number;
  onFile: (file: File) => void;
  title?: ReactNode;
  hint?: ReactNode;
  /** An error from the caller (e.g. upload failed). Validation errors are handled internally. */
  error?: ReactNode;
  disabled?: boolean;
  className?: string;
};

const formatMb = (bytes: number) => `${Math.round((bytes / 1024 / 1024) * 10) / 10}MB`;

function matchesAccept(file: File, accept: string): boolean {
  const name = file.name.toLowerCase();
  return accept
    .split(',')
    .map((a) => a.trim().toLowerCase())
    .some((a) =>
      a.startsWith('.')
        ? name.endsWith(a)
        : a.endsWith('/*')
          ? file.type.startsWith(a.slice(0, -1))
          : file.type === a,
    );
}

/** Tap to choose (opens camera / photos / files on phones); drag-and-drop on desktop. */
export function FileDropzone({
  accept,
  maxSize,
  onFile,
  title = 'Choose a file',
  hint,
  error,
  disabled,
  className,
}: FileDropzoneProps) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const take = (file: File | undefined) => {
    if (!file) return;
    if (!matchesAccept(file, accept)) {
      setLocalError("We can't read that kind of file. Try a photo, PDF, or text file.");
      return;
    }
    if (file.size > maxSize) {
      setLocalError(`That file is a bit big. Keep it under ${formatMb(maxSize)}.`);
      return;
    }
    setLocalError(null);
    onFile(file);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (!disabled) take(e.dataTransfer.files[0]);
  };

  const shownError = localError ?? error;

  return (
    <div className={className}>
      <label
        htmlFor={id}
        className={s.zone}
        data-dragging={dragging || undefined}
        data-invalid={shownError ? true : undefined}
        data-disabled={disabled || undefined}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <Icon icon={Upload} size="lg" className={s.icon} />
        <span className={s.title}>{title}</span>
        {hint ? <span className={s.hint}>{hint}</span> : null}
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          disabled={disabled}
          className={s.input}
          aria-describedby={shownError ? `${id}-error` : undefined}
          onChange={(e) => {
            take(e.currentTarget.files?.[0]);
            e.currentTarget.value = '';
          }}
        />
      </label>
      {shownError ? (
        <p id={`${id}-error`} className={cx(s.error)} role="alert">
          {shownError}
        </p>
      ) : null}
    </div>
  );
}
